import React from "react";
import clsx from "clsx";

import { Box, Divider, makeStylesSeeded } from "@origin-digital/ods-core";

import { isActiveNavSection } from "./helpers/NavHelpers";
import { KrakenNavLinkType, IKrakenNavLink } from "./interfaces";
import { NavLinkContainer } from "./NavLinkContainer";
import { navigationBarHeight } from "./helpers/consts";

const useStyles = makeStylesSeeded(({ palette }) => ({
  linkContainer: {
    display: "flex",
    minHeight: navigationBarHeight,
    paddingLeft: 0,
    paddingRight: 0,
    cursor: "pointer",
  },
  linkContainerAutoHeight: {
    minHeight: "auto",
  },
  navLinkTitle: {
    margin: 0,
    color: palette.grey["500"],
    fontSize: 14,
    lineHeight: "24px",
    display: "inline-flex",
    cursor: "pointer",
  },
  navLinkTitleActive: {
    fontWeight: 600,
  },
  navLinkTitleBold: {
    fontWeight: 600,
  },
  iconContainer: {
    display: "flex",
    position: "relative",
    pointerEvents: "none",
    "& svg": {
      fontSize: 24,
    },
    '&& [class*="MuiSvgIcon-root"]': {
      height: 38,
      margin: "0px 10px 0px 0px !important",
    },
  },
  badge: {
    minWidth: 6,
    minHeight: 6,
    borderRadius: 6,
    border: `2px solid ${palette.common.white}`,
    background: palette.primary.main,
    position: "absolute",
    top: 7,
    right: 8,
    boxSizing: "unset",
  },
  indicator: {
    width: "64px",
    lineHeight: "64px",
    display: "block",
    marginLeft: "auto",
    textAlign: "center",
    borderLeft: `solid 1px ${palette.grey["200"]}`,
    height: "100%",
    fontSize: 16,
    color: palette.primary.main,
  },
}));

export enum NavLinkIndentation {
  "None" = 0,
  "Parent" = 16,
  "Child" = 50,
}

interface INavLink extends IKrakenNavLink {
  mobile?: boolean;
  indent?: NavLinkIndentation;
  isOpen?: boolean;
}

export const NavLinkBase = ({
  badge,
  indent,
  isOpen,
  linkConfig,
  mobile,
}: INavLink) => {
  const {
    dropdownList,
    linkType,
    href,
    icon,
    title,
    divider,
    name,
    bold,
  } = linkConfig;
  const active = isActiveNavSection(href);
  const styles = useStyles();

  return (
    <>
      {divider && !mobile && (
        <Box marginBottom="medium" data-id={`${name}-divider`}>
          <Divider />
        </Box>
      )}

      <Box
        className={clsx(
          styles.linkContainer,
          linkType === KrakenNavLinkType.DROPDOWN &&
            !mobile &&
            styles.linkContainerAutoHeight
        )}
      >
        <NavLinkContainer indent={indent} href={href} id={`${name}-link`}>
          <Box className={clsx(styles.iconContainer)} data-id={`${name}-icon`}>
            {icon}
            {badge && (
              <div className={clsx(styles.badge)} data-id={`${name}-badge`} />
            )}
          </Box>
          <Box
            className={clsx(
              styles.navLinkTitle,
              active && styles.navLinkTitleActive,
              bold && styles.navLinkTitleBold
            )}
            data-id={`${name}-title`}
          >
            {title}
          </Box>
          {mobile && linkType === KrakenNavLinkType.HEADER && !!dropdownList ? (
            <Box data-id="open-indicator" className={styles.indicator}>
              {isOpen ? "-" : "+"}
            </Box>
          ) : null}
        </NavLinkContainer>
      </Box>
    </>
  );
};
